import gsap from "gsap";

const RE = /blur\((.+)?px\)/;

/**
 * Blur is re-rasterized every time the radius changes, and the work/services
 * scrubs run it on viewport-sized text and image planes. Snapping the radius
 * to this step means most scroll frames write the same string, so the write is
 * skipped and the compositor reuses the cached blurred layer.
 */
const STEP_PX = 0.5;

function filterProp() {
  return gsap.utils.checkPrefix("filter");
}

function readBlur(target: object) {
  const match = ((gsap.getProperty(target, filterProp()) as string) || "").match(RE);
  return +(match?.[1] || 0);
}

type BlurData = {
  target: HTMLElement;
  prop: string;
  start: number;
  end: number;
  baseFilter: string;
  interp: (progress: number) => string;
};

/**
 * Custom GSAP plugin from scroll-spine `app.js`.
 * Tweens `{ blur: N }` as `filter: blur(Npx)` while keeping other filter functions.
 * Render must use the `data` argument — GSAP calls this as a PropTween method.
 */
export const blurPlugin = {
  name: "blur",
  get: readBlur,
  init(this: BlurData, target: object, end: number) {
    if (!(target instanceof HTMLElement)) return false;
    const prop = filterProp();
    let current = (gsap.getProperty(target, prop) as string) || "";
    if (current === "none") current = "";
    this.target = target;
    this.prop = prop;
    this.start = readBlur(target);
    this.end = +end || 0;
    this.baseFilter = current.replace(RE, "").trim();
    const endBlur = `blur(${this.end}px)`;
    const found = current.match(RE)?.[0];
    let from: string;
    let to: string;
    if (found) {
      const i = current.indexOf(found);
      from = current;
      to = current.slice(0, i) + endBlur + current.slice(i + found.length);
    } else {
      const startPx = Number.isNaN(this.start) ? 0 : this.start;
      from = current ? `${current} blur(${startPx}px)` : `blur(${startPx}px)`;
      to = current ? `${current} ${endBlur}` : endBlur;
    }
    this.interp = gsap.utils.interpolate(from, to);
    return true;
  },
  render(progress: number, data: BlurData) {
    const el = data?.target;
    if (!el?.style) return;
    const span = data.end - data.start;
    const value = data.start + span * progress;
    /* Quantize on the radius, then map back so the rest of the filter string
     * stays in step with it. Endpoints land exactly on start/end. */
    let p = progress;
    let snapped = value;
    if (span) {
      snapped = Math.round(value / STEP_PX) * STEP_PX;
      p = (snapped - data.start) / span;
      if (progress >= 0 && progress <= 1) p = Math.min(1, Math.max(0, p));
    }
    const next =
      Math.abs(snapped) < 0.01 ? data.baseFilter || "none" : data.interp(p);
    const style = el.style as CSSStyleDeclaration & Record<string, string>;
    if (style[data.prop] === next) return;
    style[data.prop] = next;
  },
};
